"use client"

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import AnimatedText from '../ui/AnimatedText';
import { useTheme } from '../layout/ThemeProvider';
import OptimizedImage from '../ui/OptimizedImage';

// Service type definition
interface Service {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  bgImage: string;
  isIllustration?: boolean;
  category: string;
  badge?: string;
}

// Categories for services
const CATEGORIES = {
  MAINTENANCE: 'manutencao',
  DEVELOPMENT: 'desenvolvimento',
  INFRASTRUCTURE: 'infraestrutura',
  ALL: 'todos',
};

const ServicesSection = () => {
  const servicesRef = React.useRef(null);
  const { theme } = useTheme();
  const [activeCategory, setActiveCategory] = useState(CATEGORIES.MAINTENANCE);
  const [filteredServices, setFilteredServices] = useState<Service[]>([]);

  // Services array with categories - wrapped in useMemo to avoid recreating on every render
  const services = React.useMemo<Service[]>(() => [
    {
      id: 'desenvolvimento-web',
      title: 'Desenvolvimento Web',
      description: 'Criamos websites exclusivos e personalizados com foco em SEO, design moderno e segurança avançada.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      bgImage: '/images/web-development-illustration.svg',
      isIllustration: true,
      category: CATEGORIES.DEVELOPMENT,
      badge: 'Popular'
    },
    {
      id: 'desenvolvimento-sistemas',
      title: 'Desenvolvimento de Sistemas',
      description: 'Criação de sistemas personalizados, integrações, dashboards e soluções para gerenciamento empresarial.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-8m3 0v8m3-8v8" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 12h16" />
        </svg>
      ),
      bgImage: '/images/sistemas-bg.jpg',
      category: CATEGORIES.DEVELOPMENT
    },
    {
      id: 'infraestrutura-rede',
      title: 'Infraestrutura de Rede',
      description: 'Projetos completos de infraestrutura de rede para empresas de todos os portes.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      bgImage: '/images/infraestrutura-rede-illustration.svg',
      isIllustration: true,
      category: CATEGORIES.INFRASTRUCTURE
    },
    {
      id: 'energia-solar',
      title: 'Energia Solar',
      description: 'Soluções de energia solar para residências, comércios e indústrias. Instalação e manutenção de painéis fotovoltaicos com excelente custo-benefício.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      bgImage: '/images/energia-solar-bg.jpg',
      category: CATEGORIES.INFRASTRUCTURE,
      badge: 'Novo'
    },
    {
      id: 'manutencao-celulares-tablets',
      title: 'Manutenção de Celulares e Tablets',
      description: 'Serviços de reparo para smartphones e tablets de todas as marcas com diagnóstico especializado.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      bgImage: '/images/manutencao-celulares-bg.jpg',
      category: CATEGORIES.MAINTENANCE
    },
    {
      id: 'manutencao-notebooks-pcs',
      title: 'Manutenção de Notebooks e PCs',
      description: 'Diagnóstico e solução para problemas de hardware e software em computadores portáteis e desktops.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      bgImage: '/images/manutencao-pcs-bg.jpg',
      category: CATEGORIES.MAINTENANCE
    },
    {
      id: 'manutencao-videogames',
      title: 'Manutenção de Videogames',
      description: 'Reparos especializados para consoles de videogame de todas as gerações com garantia de serviço.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgImage: '/images/videogames-bg.jpg',
      category: CATEGORIES.MAINTENANCE,
      badge: 'Novidade'
    },
    {
      id: 'manutencao-eletronicos',
      title: 'Manutenção de Eletrônicos',
      description: 'Serviços de reparo para dispositivos eletrônicos diversos que necessitam de cuidados específicos.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      ),
      bgImage: '/images/manutencao-eletronicos-bg.jpg',
      category: CATEGORIES.MAINTENANCE
    },
    {
      id: 'automacao-residencial',
      title: 'Automação Residencial',
      description: 'Transforme sua casa em um ambiente inteligente com sistemas personalizados de automação e integração.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      bgImage: '/images/automacao-bg.jpg',
      category: CATEGORIES.INFRASTRUCTURE
    }
  ], []);

  // Filter services based on active category
  useEffect(() => {
    if (activeCategory === CATEGORIES.ALL) {
      setFilteredServices(services);
    } else {
      setFilteredServices(services.filter(service => service.category === activeCategory));
    }
  }, [activeCategory, services]);

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  // Category tabs for filtering
  const categoryTabs = [
    { id: CATEGORIES.MAINTENANCE, label: 'Manutenção' },
    { id: CATEGORIES.DEVELOPMENT, label: 'Desenvolvimento' },
    { id: CATEGORIES.INFRASTRUCTURE, label: 'Infraestrutura' },
    { id: CATEGORIES.ALL, label: 'Todos' }
  ];

  return (
    <section 
      id="servicos" 
      ref={servicesRef} 
      className={`py-10 md:py-16 lg:py-20 ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}
      aria-label="Serviços oferecidos"
    >
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className={`inline-block px-3 py-1 text-sm font-semibold ${
            theme === 'dark' 
              ? 'text-blue-300 bg-blue-900/50' 
              : 'text-blue-800 bg-blue-100'
          } rounded-full mb-3`}>
            Soluções Completas
          </div>
          <AnimatedText 
            text="Nossos Serviços Especializados" 
            className={`text-3xl md:text-4xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-4`}
            delay={0.2}
          />
          <p className={`text-xl ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Oferecemos soluções tecnológicas completas, desde o reparo de dispositivos 
            até o desenvolvimento de sistemas personalizados.
          </p>
        </div>
        
        {/* Category Tabs */}
        <motion.div 
          className="flex flex-wrap justify-center gap-2 mb-12"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {categoryTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveCategory(tab.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                tab.id === activeCategory
                  ? theme === 'dark'
                    ? 'bg-blue-600 text-white' 
                    : 'bg-blue-500 text-white'
                  : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              aria-current={tab.id === activeCategory ? 'page' : undefined}
            >
              {tab.label}
            </button>
          ))}
        </motion.div>
        
        {/* Cards de serviços com design horizontal */}
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="grid md:grid-cols-2 gap-6 lg:gap-8"
        >
          {filteredServices.map((service, index) => (
            <motion.div 
              key={service.id}
              variants={fadeIn}
              className="group"
              whileHover={{ scale: 1.01 }}
            >
              <Link href={`/servicos/${service.id}`} className="block no-underline">
                <motion.div
                  whileHover={{ 
                    y: -5,
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" 
                  }}
                  className={`${
                    theme === 'dark' 
                      ? 'bg-gray-800 text-white border border-gray-700' 
                      : 'bg-white border border-gray-200'
                  } rounded-xl shadow-md overflow-hidden transition-all relative h-full cursor-pointer`}
                >
                  {/* Background Service Image - Agora otimizada */}
                  {service.bgImage && (
                    <div className="absolute inset-0 opacity-10 z-0 overflow-hidden">
                      <OptimizedImage
                        src={service.bgImage}
                        alt={`Imagem de fundo ${service.title}`}
                        width={400}
                        height={300}
                        className="w-full h-full object-cover"
                        isLCP={index === 0 && activeCategory === CATEGORIES.MAINTENANCE}
                      />
                    </div>
                  )}
                  
                  {/* Badge */}
                  {service.badge && (
                    <div className={`absolute top-4 right-4 z-20 px-3 py-1 text-xs font-bold rounded-full 
                      ${theme === 'dark' 
                        ? 'bg-gradient-to-r from-blue-600 to-purple-500 text-white' 
                        : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                      }`}>
                      {service.badge}
                    </div>
                  )}
                  
                  {/* Top border gradient on hover */}
                  <div 
                    className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-purple-500 
                      transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}
                    aria-hidden="true"
                  ></div>
                  
                  <div className="p-6 flex flex-col md:flex-row gap-5 relative z-10 h-full">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-14 h-14 rounded-xl ${
                      theme === 'dark' 
                        ? 'bg-gradient-to-br from-blue-900 to-purple-900 text-blue-300' 
                        : 'bg-gradient-to-br from-blue-50 to-purple-100 text-blue-600'
                    } flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      {service.icon}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <h3 className={`text-xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      } mb-2 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors`}>
                        {service.title}
                      </h3>
                      <p className={`${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                      } mb-4 line-clamp-2`}>
                        {service.description}
                      </p>
                      
                      {/* Link to service page */}
                      <div className="flex items-center text-blue-500 font-medium group-hover:text-blue-400 transition-colors">
                        <span>Saiba mais</span>
                        <svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;